import {
  createContext,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { Search } from 'lucide-react';

import { ErrorState } from '@/components/StateBlock';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useLocaleOptional } from '@/i18n/locale';

import type { PaletteBodyProps } from './CommandPaletteBody';

/*
 * The ⌘K palette (ui-ux.md §5 "Global search / jump-to"): one box for titles
 * (`/v1/shows/search`), people (`/v1/users/search`), your lists (client
 * filter over `/v1/lists`) and the app's destinations. Built on the restyled
 * shadcn `Command` (cmdk) inside the `Dialog` primitive — Radix traps focus,
 * closes on Esc, and returns focus to whatever opened it (the rail trigger,
 * or the element that had focus when ⌘K / Ctrl+K / `/` was pressed).
 *
 * Code-split: this file (provider, shortcuts, dialog frame) is in the main
 * bundle; the content — cmdk and its queries — is the lazy
 * `CommandPaletteBody` chunk, fetched on first open or on hover/focus of the
 * rail's search row. While it loads, a stand-in field with the same look
 * holds focus and keeps what is typed, so the first keystrokes after ⌘K are
 * never lost.
 */

type BodyModule = { default: ComponentType<PaletteBodyProps> };

let bodyModule: Promise<BodyModule> | null = null;

/** Start loading the palette chunk (idempotent). A failed load can retry. */
// eslint-disable-next-line react-refresh/only-export-components
export function preloadCommandPalette(): Promise<BodyModule> {
  bodyModule ??= import('./CommandPaletteBody')
    .then((m) => ({ default: m.PaletteBody }))
    .catch((error: unknown) => {
      bodyModule = null;
      throw error;
    });
  return bodyModule;
}

interface PaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const PaletteContext = createContext<PaletteContextValue | null>(null);

/** True on Apple platforms, where the shortcut is ⌘K rather than Ctrl K. */
// eslint-disable-next-line react-refresh/only-export-components
export function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const platform = nav.userAgentData?.platform ?? nav.platform ?? nav.userAgent ?? '';
  return /mac|iphone|ipad|ipod/i.test(platform);
}

/** The shortcut as displayed: `⌘K` on Apple platforms, `Ctrl K` elsewhere. */
// eslint-disable-next-line react-refresh/only-export-components
export function paletteShortcut(): { label: string; aria: string } {
  return isApplePlatform()
    ? { label: '⌘K', aria: 'Meta+K' }
    : { label: 'Ctrl K', aria: 'Control+K' };
}

/** Open/close the palette from anywhere inside the app shell. */
// eslint-disable-next-line react-refresh/only-export-components
export function useCommandPalette(): PaletteContextValue {
  return useContext(PaletteContext) ?? { open: false, setOpen: () => {} };
}

/** True when a single-key shortcut would hijack typing (inputs, textareas, contenteditable). */
// eslint-disable-next-line react-refresh/only-export-components
export function isTextField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Owns the palette's open state and the global shortcuts: ⌘K / Ctrl+K
 * toggles it anywhere in the shell; `/` opens it unless focus is in a text
 * field (so typing a `/` in a comment never hijacks the keyboard).
 */
export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
        return;
      }
      if (
        event.key === '/' &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !isTextField(event.target)
      ) {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const value = useMemo(() => ({ open, setOpen }), [open]);
  return (
    <PaletteContext.Provider value={value}>
      {children}
      <CommandPalette open={open} onOpenChange={setOpen} />
    </PaletteContext.Provider>
  );
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const { t } = useLocaleOptional();
  // Opened programmatically (no Radix `Dialog.Trigger`), so Radix has no
  // trigger to restore focus to — remember what had focus and restore it.
  const returnFocusRef = useRef<HTMLElement | null>(null);
  // Typed into the stand-in field while the chunk loads; handed to the body.
  const [draft, setDraft] = useState('');
  const [standInShown, setStandInShown] = useState(false);
  // Bumped by Retry after a failed chunk load: a fresh lazy() re-imports.
  const [attempt, setAttempt] = useState(0);
  const Body = useMemo(
    () =>
      lazy(() =>
        preloadCommandPalette().catch(() => ({
          default: () => <PaletteLoadError onRetry={() => setAttempt((n) => n + 1)} />,
        })),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attempt],
  );

  // A closed palette starts empty next time (⌘K also closes it directly).
  useEffect(() => {
    if (open) return;
    setDraft('');
    setStandInShown(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* No open/close motion: a keyboard-summoned, many-times-a-day surface
          appears and leaves instantly (ui-ux.md §4 Rules). */}
      <DialogContent
        className="w-[calc(100%-2rem)] max-w-xl gap-0 overflow-hidden p-0 !animate-none"
        overlayClassName="!animate-none"
        onOpenAutoFocus={() => {
          const active = document.activeElement;
          returnFocusRef.current =
            active instanceof HTMLElement && active !== document.body ? active : null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          const target = returnFocusRef.current;
          returnFocusRef.current = null;
          if (target?.isConnected) target.focus();
        }}
      >
        <DialogTitle className="sr-only">{t('palette.title')}</DialogTitle>
        <DialogDescription className="sr-only">{t('palette.description')}</DialogDescription>
        {/* Mounted only while open, so its queries run only when someone searches. */}
        {open ? (
          <Suspense
            fallback={
              <PaletteStandIn
                value={draft}
                onChange={setDraft}
                onShown={setStandInShown}
              />
            }
          >
            <Body
              onDone={() => onOpenChange(false)}
              initialQuery={draft}
              takeFocus={standInShown}
            />
          </Suspense>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}


/**
 * Shown for the moment the palette chunk is loading: the same input row and
 * fixed-height list as the real palette (no jump when it swaps in), taking
 * the dialog's auto-focus so what the user types lands here and carries over.
 */
function PaletteStandIn({
  value,
  onChange,
  onShown,
}: {
  value: string;
  onChange: (value: string) => void;
  onShown: (shown: true) => void;
}) {
  const { t } = useLocaleOptional();
  useEffect(() => {
    onShown(true);
  }, [onShown]);
  return (
    <div className="flex flex-col rounded-lg bg-surface text-ink">
      <div className="flex items-center border-b border-border px-3">
        <Search className="mr-2 h-4 w-4 shrink-0 text-ink-tertiary" strokeWidth={1.75} aria-hidden />
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-label={t('palette.inputA11y')}
          placeholder={t('palette.placeholder')}
          className="flex h-11 w-full rounded-sm bg-transparent py-3 pr-10 text-body text-ink outline-none placeholder:text-ink-tertiary"
        />
      </div>
      <div aria-hidden className="h-[min(60dvh,420px)]" />
    </div>
  );
}

/** The chunk failed to load (offline, or a stale deploy): say so, offer Retry. */
function PaletteLoadError({ onRetry }: { onRetry: () => void }) {
  const { t } = useLocaleOptional();
  return (
    <div className="flex h-[min(60dvh,420px)] items-center justify-center p-4">
      <ErrorState size="inline" title={t('discover.paletteLoadError')} onRetry={onRetry} />
    </div>
  );
}
